// src/pages/admin/analytics/components/EditWastageModal.jsx
import { useState, useEffect } from 'react';
import { X, AlertCircle, Save, Calendar, Building2, Package, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import analyticsService from '../../../../services/analyticsService';

export const WASTAGE_REASONS = [
  { value: 'trimming_prep', label: 'Chicken / Trimming / Prep Loss' },
  { value: 'expiration', label: 'Expired Product' },
  { value: 'spoilage', label: 'Spoilage' },
  { value: 'damaged', label: 'Damaged / Spillage' },
  { value: 'quality_rejection', label: 'Quality Rejection' },
  { value: 'other', label: 'Other' },
];

const formatForDateTimeInput = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const EditWastageModal = ({ isOpen, onClose, onSuccess, log }) => {
  const [quantity, setQuantity] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [reason, setReason] = useState('trimming_prep');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && log) {
      setQuantity(log.quantity !== undefined ? String(log.quantity) : '');
      setDateTime(formatForDateTimeInput(log.createdAt));
      setReason(log.reason || 'trimming_prep');
      setNotes(log.notes || '');
    }
  }, [isOpen, log]);

  if (!isOpen || !log) return null;

  const isRM = log.itemType === 'raw_material';
  const itemName = isRM ? log.rawMaterial?.name : log.product?.name;
  const itemCode = isRM ? log.rawMaterial?.code : log.product?.sku;
  const unitCost = log.unitCost || 0;
  const originalQty = Number(log.quantity || 0);

  const numQty = parseFloat(quantity);
  const validQty = !isNaN(numQty) && numQty > 0;
  const diffQty = validQty ? numQty - originalQty : 0;
  const calculatedTotalCost = validQty ? numQty * unitCost : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validQty) {
      toast.error('Please enter a valid positive quantity');
      return;
    }

    try {
      setLoading(true);
      await analyticsService.updateWastageEntry(log._id, {
        quantity: numQty,
        reason,
        notes: notes.trim(),
        createdAt: dateTime ? new Date(dateTime).toISOString() : undefined,
      });

      toast.success('Wastage entry updated successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to update wastage entry:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to update wastage entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-gray-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-red-700 via-rose-700 to-orange-700 px-6 py-5 text-white flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white/15 rounded-xl backdrop-blur-sm">
              <Package className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black tracking-tight">Edit Wastage Entry</h3>
              <p className="text-xs text-rose-100">Modify date, quantity, reason, or staff remarks</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 rounded-xl text-rose-100 hover:text-white hover:bg-white/20 transition-all"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Item Details Summary Card */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-2">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      isRM ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {isRM ? 'Raw Material' : 'Finished Product'}
                  </span>
                  {itemCode && (
                    <span className="text-[10px] text-gray-500 font-mono">
                      SKU: {itemCode}
                    </span>
                  )}
                </div>
                <h4 className="text-base font-bold text-gray-900 mt-1">{itemName || 'Item Details'}</h4>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-semibold text-gray-500 uppercase">Unit Cost</p>
                <p className="text-sm font-black text-gray-900">Rs. {unitCost.toFixed(2)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-200 text-xs text-gray-600">
              <div className="flex items-center space-x-1.5">
                <Building2 className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="truncate">{log.branch?.name || 'All Branches'}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span>
                  {new Date(log.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Date & Time Field */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Date & Time Logged *
            </label>
            <div className="relative">
              <input
                type="datetime-local"
                required
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                className="w-full px-4 py-2.5 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none"
              />
            </div>
          </div>

          {/* Quantity Field */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Wastage Quantity ({log.unitName || 'units'}) *
              </label>
              <span className="text-xs text-gray-500">
                Original: <span className="font-bold text-gray-700">{originalQty} {log.unitName || 'units'}</span>
              </span>
            </div>
            <div className="relative">
              <input
                type="number"
                step="any"
                min="0.001"
                required
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Enter quantity"
                className="w-full px-4 py-2.5 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none"
              />
              <span className="absolute right-3 top-2.5 text-xs font-bold text-gray-400 uppercase">
                {log.unitName || 'units'}
              </span>
            </div>

            {/* Inventory impact indicator */}
            {validQty && diffQty !== 0 && (
              <div
                className={`mt-2 p-2.5 rounded-lg text-xs flex items-center space-x-2 ${
                  diffQty > 0
                    ? 'bg-amber-50 border border-amber-200 text-amber-800'
                    : 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                }`}
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {diffQty > 0
                    ? `Quantity increased by +${diffQty.toFixed(2)} ${log.unitName || 'units'}. Stock will be deducted accordingly.`
                    : `Quantity decreased by ${Math.abs(diffQty).toFixed(2)} ${log.unitName || 'units'}. Stock will be restored back.`}
                </span>
              </div>
            )}
          </div>

          {/* Live Total Wastage Cost preview */}
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 flex items-center justify-between">
            <span className="text-xs font-bold text-rose-900 uppercase">Calculated Wastage Cost</span>
            <span className="text-base font-black text-rose-700">
              Rs. {calculatedTotalCost.toFixed(2)}
            </span>
          </div>

          {/* Reason Field */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Wastage Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-4 py-2.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none"
            >
              {WASTAGE_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          {/* Notes / Staff Remarks */}
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Notes / Staff Remarks
            </label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., 200g bone trimming loss, damaged packaging, accidental drop..."
              className="w-full px-4 py-2.5 text-sm text-gray-800 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none resize-none"
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2.5 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !validQty}
              className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWastageModal;
